import React from "react";
import api from "../../api/http";
import { API_ENDPOINTS } from "../../api/endpoints";
import { useLanguage } from "../../context/LanguageContext";

export const PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県", "茨城県", "栃木県", "群馬県",
  "埼玉県", "千葉県", "東京都", "神奈川県", "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県",
  "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県",
  "鳥取県", "島根県", "岡山県", "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県", "福岡県",
  "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

export const EMPTY_PROFILE = {
  companyName: "",
  personName: "",
  postalCode: "",
  prefecture: "",
  city: "",
  street: "",
  building: "",
  phoneNumber: "",
};

const toHalfWidthDigits = (value) =>
  String(value || "").replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));

export const normalizePostalCode = (value) => {
  const digits = toHalfWidthDigits(value).replace(/[^0-9]/g, "");
  return digits.length === 7 ? digits : null;
};

const formatPostalInput = (value) => {
  const digits = toHalfWidthDigits(value).replace(/[^0-9]/g, "").slice(0, 7);
  return digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
};

/** Billing name shown on invoices: company, or the person for individuals. */
export const billingNameOf = (profile) =>
  (profile?.companyName || "").trim() || (profile?.personName || "").trim();

/**
 * 請求先情報 form with postal-code address lookup (郵便番号 → 都道府県・市区町村・町域).
 * The customer then only types the street number and building.
 */
export default function BillingProfileForm({
  initialValues,
  submitLabel,
  onSubmit,
  onCancel,
  disabled = false,
}) {
  const { text } = useLanguage();
  const [values, setValues] = React.useState(() => ({
    ...EMPTY_PROFILE,
    ...(initialValues || {}),
    postalCode: formatPostalInput(initialValues?.postalCode || ""),
  }));
  const [errors, setErrors] = React.useState({});
  const [submitError, setSubmitError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [lookup, setLookup] = React.useState({ state: "idle", towns: [] });
  const lastLookupRef = React.useRef(normalizePostalCode(initialValues?.postalCode) || "");
  const streetRef = React.useRef(null);

  const set = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const applyResult = React.useCallback((result) => {
    setValues((prev) => ({ ...prev, prefecture: result.prefecture, city: `${result.city}${result.town}` }));
    setErrors((prev) => ({ ...prev, prefecture: undefined, city: undefined }));
    // The customer only needs to add the street number now.
    setTimeout(() => streetRef.current?.focus(), 0);
  }, []);

  const runLookup = React.useCallback(async (code) => {
    lastLookupRef.current = code;
    setLookup({ state: "loading", towns: [] });
    try {
      const res = await api.get(API_ENDPOINTS.POSTAL_CODE_LOOKUP, { params: { code } });
      if (lastLookupRef.current !== code) return;
      const results = res.data?.data?.results || [];
      if (results.length === 0) {
        setLookup({ state: "notFound", towns: [] });
        return;
      }
      applyResult(results[0]);
      setLookup({ state: results.length > 1 ? "multiple" : "found", towns: results });
    } catch (_) {
      if (lastLookupRef.current === code) setLookup({ state: "error", towns: [] });
    }
  }, [applyResult]);

  const onPostalChange = (e) => {
    const formatted = formatPostalInput(e.target.value);
    setValues((prev) => ({ ...prev, postalCode: formatted }));
    setErrors((prev) => ({ ...prev, postalCode: undefined }));
    const code = normalizePostalCode(formatted);
    if (code && code !== lastLookupRef.current) runLookup(code);
    if (!code) setLookup({ state: "idle", towns: [] });
  };

  const validate = () => {
    const next = {};
    const required = text("入力してください", "Required");
    if (!values.personName.trim()) next.personName = required;
    if (!normalizePostalCode(values.postalCode)) next.postalCode = text("7桁の数字で入力してください", "Enter 7 digits");
    if (!values.prefecture) next.prefecture = text("選択してください", "Select one");
    if (!values.city.trim()) next.city = required;
    if (!values.street.trim()) next.street = required;
    if (!values.phoneNumber.trim()) next.phoneNumber = required;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || disabled) return;
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        companyName: values.companyName.trim(),
        personName: values.personName.trim(),
        postalCode: normalizePostalCode(values.postalCode),
        prefecture: values.prefecture,
        city: values.city.trim(),
        street: values.street.trim(),
        building: values.building.trim(),
        phoneNumber: values.phoneNumber.trim(),
      });
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || err?.response?.data?.error || err?.message ||
        text("保存できませんでした。もう一度お試しください。", "Could not save. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const lookupMessage = {
    loading: text("住所を検索しています…", "Looking up the address…"),
    notFound: text("該当する住所が見つかりません。郵便番号をご確認いただくか、住所を直接入力してください。", "No address found. Check the postal code or type the address."),
    error: text("住所を自動入力できませんでした。直接入力してください。", "Couldn't fill in the address. Please type it."),
  }[lookup.state];

  const field = (name, label, input, hint) => (
    <div style={styles.field}>
      <label style={styles.label} htmlFor={`bp-${name}`}>{label}</label>
      {input}
      {hint && !errors[name] && <div style={styles.hint}>{hint}</div>}
      {errors[name] && <div style={styles.error}>{errors[name]}</div>}
    </div>
  );

  const inputProps = (name) => ({
    id: `bp-${name}`,
    value: values[name],
    onChange: set(name),
    style: { ...styles.input, ...(errors[name] ? styles.inputError : null) },
    disabled: submitting || disabled,
  });

  const req = <span style={styles.required}> *</span>;
  const opt = <span style={styles.optional}>{text("（任意）", " (optional)")}</span>;

  return (
    <form onSubmit={handleSubmit} noValidate>
      {field(
        "companyName",
        <>{text("会社名・屋号", "Company name")}{opt}</>,
        <input {...inputProps("companyName")} autoComplete="organization" placeholder={text("例：株式会社ピシアス", "e.g. PITHIAS Inc.")} />,
        text("個人でご利用の場合は空欄で構いません（請求書は氏名宛てになります）。", "Leave blank for individuals; invoices will be addressed to your name.")
      )}
      {field(
        "personName",
        <>{text("氏名（ご担当者名）", "Your name")}{req}</>,
        <input {...inputProps("personName")} autoComplete="name" placeholder={text("例：山田 太郎", "e.g. Taro Yamada")} />
      )}
      <div style={styles.row}>
        {field(
          "postalCode",
          <>{text("郵便番号", "Postal code")}{req}</>,
          <input
            {...inputProps("postalCode")}
            onChange={onPostalChange}
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="261-0011"
            maxLength={8}
          />,
          lookupMessage || text("入力すると住所を自動で入力します。", "The address fills in automatically.")
        )}
        {field(
          "prefecture",
          <>{text("都道府県", "Prefecture")}{req}</>,
          <select {...inputProps("prefecture")} autoComplete="address-level1">
            <option value="">{text("選択してください", "Select")}</option>
            {PREFECTURES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>
      {lookup.state === "multiple" && (
        <div style={styles.field}>
          <label style={styles.label} htmlFor="bp-town">{text("町域を選択", "Select the area")}</label>
          <select
            id="bp-town"
            style={styles.input}
            onChange={(e) => {
              const r = lookup.towns[Number(e.target.value)];
              if (r) applyResult(r);
            }}
            defaultValue="0"
          >
            {lookup.towns.map((r, i) => <option key={`${r.city}${r.town}${i}`} value={i}>{`${r.city}${r.town}`}</option>)}
          </select>
        </div>
      )}
      {field(
        "city",
        <>{text("市区町村・町域", "City and area")}{req}</>,
        <input {...inputProps("city")} autoComplete="address-level2" placeholder={text("例：千葉市美浜区真砂", "e.g. 千葉市美浜区真砂")} />
      )}
      {field(
        "street",
        <>{text("番地", "Street number")}{req}</>,
        <input {...inputProps("street")} ref={streetRef} autoComplete="address-line1" placeholder={text("例：2-15-3", "e.g. 2-15-3")} />
      )}
      {field(
        "building",
        <>{text("建物名・部屋番号", "Building, room")}{opt}</>,
        <input {...inputProps("building")} autoComplete="address-line2" placeholder={text("例：ピシアスビル 809", "e.g. PITHIAS Bldg 809")} />
      )}
      {field(
        "phoneNumber",
        <>{text("電話番号", "Phone number")}{req}</>,
        <input {...inputProps("phoneNumber")} type="tel" autoComplete="tel" placeholder="03-1234-5678" />
      )}

      {submitError && <div style={styles.submitError} role="alert">{submitError}</div>}

      <div style={styles.actions}>
        {onCancel && (
          <button type="button" style={styles.cancelBtn} onClick={onCancel} disabled={submitting}>
            {text("キャンセル", "Cancel")}
          </button>
        )}
        <button type="submit" style={styles.submitBtn} disabled={submitting || disabled}>
          {submitting ? text("処理中…", "Saving…") : submitLabel || text("保存", "Save")}
        </button>
      </div>
    </form>
  );
}

const styles = {
  row: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#1f2937", marginBottom: 4 },
  required: { color: "#dc2626" },
  optional: { color: "#6b7280", fontWeight: 400 },
  input: {
    width: "100%",
    padding: "9px 10px",
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontSize: 15,
    boxSizing: "border-box",
  },
  inputError: { borderColor: "#dc2626" },
  hint: { fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.5 },
  error: { fontSize: 12, color: "#dc2626", marginTop: 4, lineHeight: 1.5 },
  submitError: { color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", fontSize: 13, marginBottom: 12 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 },
  cancelBtn: { padding: "9px 18px", background: "#ffffff", color: "#374151", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  submitBtn: { padding: "9px 22px", background: "#1f63d6", color: "#ffffff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600 },
};
